import {
	CRUISE_DATES_FILTER,
	DEFAULT_PROMO_TITLE,
	DEPARTURE_PORT_FILTER,
	EXTERNAL_FILTERS,
	PROMO_KEY,
} from 'components/CruiseSearchWidget/constants';
import { CalendarType } from 'components/CruiseSearchWidget/types';
import { isUniqueFilter } from 'components/CruiseSearchWidget/utils/reducers';
import { OCCUPANCY_TYPE } from 'components/NumberOfGuests/constants';
import {
	CUSTOM_FILTERS_KEY,
	DateRangeFilter,
	GraphQLFilterTypes,
	GroupListFilter,
	GroupListFilterOption,
	ListFilter,
	ListFilterOption,
} from 'lib/apiClient';
import { Locale } from 'lib/config/config';
import {
	OcupancyFilter,
	UpdateSelectionItem,
	getAffectedFilter,
	getContainersForFilterValue,
	getGroupForFilterValue,
	getYearAndCalendarType,
} from 'lib/mappers';
import { FilterOption, Filters, Promotion, SelectedOption, Selection, TypedSelection } from '../../types';
import { MonthGroup, MonthsGrouping, getDateRangeLabel, getMonthGroupsByYear } from '../dates';
import { ExternalOptionsToHistoricalSelectionProps } from '../types';

export function addRemoveListOption(selectedOption: SelectedOption, historicalSelection: Array<SelectedOption>): Array<SelectedOption> {
	const isDeparturePortFilter = selectedOption.filterKey === DEPARTURE_PORT_FILTER.key;

	const optionIsSelected = historicalSelection.find((selection) => {
		return (
			selection.filterKey === selectedOption?.filterKey &&
			(!isDeparturePortFilter ? selection.group == selectedOption.group : true) && // ignore group for departure port filter in case of duplicated ports (River/Ocean). e.g. AMS
			selection.option.code == selectedOption?.option?.code &&
			selection.containers === selectedOption.containers
		);
	});

	if (optionIsSelected) {
		return historicalSelection.filter(
			(selection) => !(selection.filterKey == selectedOption?.filterKey && selection.option.code == selectedOption?.option.code),
		);
	}

	if (isUniqueFilter(selectedOption)) {
		return [selectedOption];
	}

	return [...historicalSelection, selectedOption];
}

function updateSelectionByListFilter(
	filter: FilterOption,
	selectedValue: UpdateSelectionItem,
	historicalSelection: Array<SelectedOption>,
	isPromo: boolean = false,
): Array<SelectedOption> {
	let selectedOptions: ListFilterOption[] = [];
	const isDeparturePortFilter = filter.key === DEPARTURE_PORT_FILTER.key;

	if (filter?.type == GraphQLFilterTypes.LIST || filter?.type == GraphQLFilterTypes.NUMBER_RANGE) {
		selectedOptions = (filter as ListFilter).options.filter((option) => {
			if (isPromo) {
				return selectedValue.value.every((promo) => option.criteria?.promo?.includes(promo));
			}

			return selectedValue.value.includes(option.code);
		});
	} else if (filter?.type == GraphQLFilterTypes.GROUP_LIST) {
		const options = (filter as GroupListFilter).groups.reduce(
			(acum: ListFilterOption[], group: GroupListFilterOption) => [...acum, ...(group?.options ?? [])],
			[],
		);
		selectedOptions = options.filter((option) => selectedValue.value.includes(option.code));
	}

	if (isDeparturePortFilter) {
		// Remove duplicated ports (ocean/river) from selectedOptions
		selectedOptions = selectedOptions.filter((option, index, self) => index === self.findIndex((t) => t.code === option.code));
	}

	if (selectedOptions.length === 0) return historicalSelection;

	const optionsToUpdate: SelectedOption[] = selectedOptions.map((option) => ({
		filterKey: filter.key,
		group: selectedValue.group,
		containers: selectedValue.containers,
		criteria: selectedValue.criteria,
		option: {
			code: option.code,
			label: option.label,
		},
	}));

	return optionsToUpdate.reduce((updatedHistoricalSelection, option) => addRemoveListOption(option, updatedHistoricalSelection), historicalSelection);
}

function updateSelectionByOccupancyFilter(
	filter: OcupancyFilter,
	selectedValue: UpdateSelectionItem,
	historicalSelection: Array<SelectedOption>,
): Array<SelectedOption> {
	let optionsToUpdate: SelectedOption[] = [...historicalSelection];

	const selectedOption = historicalSelection.filter((option) => option.filterKey === filter.key);

	if (selectedOption.length) {
		optionsToUpdate = historicalSelection.filter((option) => option.filterKey !== filter.key);
	} else {
		optionsToUpdate.push({
			filterKey: filter.key,
			option: {
				code: selectedValue.value.toString(),
				label: filter.label,
			},
		});
	}

	return optionsToUpdate;
}

export function regroupSelectedMonths(
	historicalSelection: Array<SelectedOption>,
	monthGroup: MonthGroup,
	selectedValue: UpdateSelectionItem,
	locale: Locale,
): Array<SelectedOption> {
	const newHistoricalSelection = historicalSelection.filter((itemSelection) => {
		if (itemSelection.year === selectedValue.year && itemSelection.option.dateRanges?.some((dateRange) => monthGroup.dateRanges.includes(dateRange)))
			return false;
		return true;
	});

	newHistoricalSelection.push({
		filterKey: CRUISE_DATES_FILTER.key,
		year: selectedValue.year,
		calendarType: CalendarType.Months,
		option: {
			code: monthGroup.code[0],
			label: monthGroup.label[0],
			dateRanges: monthGroup.dateRanges,
			formattedLabel: getDateRangeLabel([monthGroup.startRange, monthGroup.endRange].join('~'), locale),
		},
	});

	return newHistoricalSelection;
}

export function splitRemoveMonthGroups(
	selectedValue: UpdateSelectionItem,
	historicalSelection: Array<SelectedOption>,
	monthGroups: Array<MonthsGrouping>,
	locale: Locale,
): Array<SelectedOption> {
	let originalGroupSelection: SelectedOption;
	const splitedGroups: SelectedOption[] = [];
	const year = monthGroups.find((year) => year.year === selectedValue.year);

	const newHistoricalSelection = historicalSelection.filter((selectionItem) => {
		if (selectionItem.year === selectedValue.year && selectedValue.value.some((value) => selectionItem.option.dateRanges?.includes(value))) {
			originalGroupSelection = selectionItem;
			return false;
		}

		return true;
	});

	year?.monthGroups.forEach((group) => {
		if (group.dateRanges.every((dateRange) => originalGroupSelection.option.dateRanges?.includes(dateRange))) {
			splitedGroups.push({
				filterKey: CRUISE_DATES_FILTER.key,
				year: selectedValue.year,
				calendarType: CalendarType.Months,
				option: {
					code: group.code[0],
					label: group.label[0],
					dateRanges: group.dateRanges,
					formattedLabel: getDateRangeLabel([group.startRange, group.endRange].join('~'), locale),
				},
			});
		}
	});

	if (splitedGroups.length > 0) newHistoricalSelection.push(...splitedGroups);

	return newHistoricalSelection;
}

function removeCurrentDatesSelection(historicalSelection: Array<SelectedOption>, calendarSelectionToRemove: CalendarType): Array<SelectedOption> {
	return historicalSelection.filter((selection) => selection.calendarType !== calendarSelectionToRemove);
}

export function addRemoveDateRangeSelection(
	historicalSelection: Array<SelectedOption>,
	selectedValue: UpdateSelectionItem,
	locale: Locale,
): Array<SelectedOption> {
	let newHistoricalSelection = [...historicalSelection];

	const [dateRange] = selectedValue.value;
	const datesRangeExist = historicalSelection.some((item) => item.option.dateRanges?.includes(dateRange));

	if (datesRangeExist) {
		return historicalSelection.filter((item) => !item.option.dateRanges?.includes(dateRange));
	}

	newHistoricalSelection = removeCurrentDatesSelection(historicalSelection, CalendarType.Dates);

	const formattedLabel = getDateRangeLabel(dateRange, locale);
	newHistoricalSelection.push({
		filterKey: CRUISE_DATES_FILTER.key,
		year: selectedValue.year,
		calendarType: CalendarType.Dates,
		option: {
			code: selectedValue.value[0],
			label: formattedLabel ?? '',
			dateRanges: [dateRange],
			formattedLabel: formattedLabel,
		},
	});
	return newHistoricalSelection;
}

export function updateSelectionByDateFilter(
	filter: DateRangeFilter,
	selectedValue: UpdateSelectionItem,
	historicalSelection: Array<SelectedOption>,
	locale: Locale,
): Array<SelectedOption> {
	let newHistoricalSelection: Array<SelectedOption> = [...historicalSelection];

	if (selectedValue.calendarType === CalendarType.Months) {
		const monthGroups = getMonthGroupsByYear(filter.years);
		const year = monthGroups.find((year) => year.year == selectedValue.year) as MonthsGrouping;
		const group = year?.monthGroups.find((group) => selectedValue.value.some((value) => group.dateRanges.includes(value))) as MonthGroup;

		newHistoricalSelection = removeCurrentDatesSelection(historicalSelection, CalendarType.Dates);
		newHistoricalSelection = group
			? regroupSelectedMonths(newHistoricalSelection, group, selectedValue, locale)
			: splitRemoveMonthGroups(selectedValue, newHistoricalSelection, monthGroups, locale);
	} else if (selectedValue.calendarType === CalendarType.Dates) {
		newHistoricalSelection = removeCurrentDatesSelection(historicalSelection, CalendarType.Months);
		newHistoricalSelection = addRemoveDateRangeSelection(newHistoricalSelection, selectedValue, locale);
	}

	return newHistoricalSelection;
}

export function getNewHistoricalSelection(
	filter: FilterOption | undefined,
	selectedValue: UpdateSelectionItem,
	historicalSelection: Array<SelectedOption>,
	locale: Locale,
	isPromo: boolean = false,
): Array<SelectedOption> {
	if (!filter) {
		return historicalSelection.filter((item) => !selectedValue.value.includes(item.option.code));
	}

	let newHistoricalSelection: Array<SelectedOption> = [];

	if (filter.type == GraphQLFilterTypes.LIST || filter.type == GraphQLFilterTypes.NUMBER_RANGE || filter.type == GraphQLFilterTypes.GROUP_LIST) {
		newHistoricalSelection = updateSelectionByListFilter(filter, selectedValue, historicalSelection, isPromo);
	} else if (filter.type == GraphQLFilterTypes.DATE_RANGE) {
		newHistoricalSelection = updateSelectionByDateFilter(filter as DateRangeFilter, selectedValue, historicalSelection, locale);
	} else if (filter.type === OCCUPANCY_TYPE) {
		newHistoricalSelection = updateSelectionByOccupancyFilter(filter as OcupancyFilter, selectedValue, historicalSelection);
	}
	return newHistoricalSelection;
}

export const initialSelectionToHistoricalSelection = (
	filters: Filters,
	initialSelection: Selection,
	locale: Locale,
	promotion?: Promotion,
): Array<SelectedOption> => {
	return Object.keys(initialSelection).reduce((historicalSelection: Array<SelectedOption>, filterKey: string) => {
		const selectedOptions = initialSelection[filterKey];
		const currentFilter = getAffectedFilter(filters, filterKey, selectedOptions);

		if (currentFilter) {
			return selectedOptions.reduce(
				(newHistoricalSelection: Array<SelectedOption>, value: string) => {
					let group: string | undefined;
					let year: string | undefined;
					let calendarType: CalendarType = '' as CalendarType;
					let containers: Array<string> | undefined;
					let criteria: TypedSelection | undefined;

					if (currentFilter.type === GraphQLFilterTypes.GROUP_LIST) {
						group = getGroupForFilterValue(currentFilter as GroupListFilter, value);
					} else if (currentFilter.type === GraphQLFilterTypes.DATE_RANGE) {
						({ year, calendarType } = getYearAndCalendarType(currentFilter as DateRangeFilter, value));
					} else if (currentFilter.key === CUSTOM_FILTERS_KEY) {
						({ containers, criteria } = getContainersForFilterValue(currentFilter as ListFilter, value, filterKey === PROMO_KEY));
					}

					return getNewHistoricalSelection(
						currentFilter,
						{ value: [value], group, year, calendarType, containers, criteria },
						newHistoricalSelection,
						locale,
						filterKey === PROMO_KEY,
					);
				},
				[...historicalSelection],
			);
		}

		if (!currentFilter && EXTERNAL_FILTERS.includes(filterKey)) {
			return addExternalParamsToHistoricalSelection({ historicalSelection, promotion });
		}

		return historicalSelection;
	}, []);
};

const addExternalParamsToHistoricalSelection = ({
	historicalSelection,
	promotion,
}: ExternalOptionsToHistoricalSelectionProps): Array<SelectedOption> => {
	const mappedAppliedPromotion = promotion ? mapPromotionToHistoricalSelection(promotion) : [];

	return [...historicalSelection, ...mappedAppliedPromotion];
};

const mapPromotionToHistoricalSelection = ({ valid, code, title }: Promotion): Array<SelectedOption> => {
	if (!valid) {
		return [];
	}

	return [
		{
			filterKey: PROMO_KEY,
			isExternal: true,
			option: {
				code,
				label: title || DEFAULT_PROMO_TITLE,
			},
		},
	];
};
